// src/pages/Hive/Hive.tsx
import {useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useCurrentHiveStore} from 'entities/currentHive/model/store';
import {HiveBodyAnalytics, HiveBodySummary, HiveChartSection, HiveHeader} from 'features/hive';
import {usePageTitleStore} from 'entities/pageTitle';
import {useBreadcrumbsStore} from 'entities/breadcrumbs';
import {HiveBodyWrapper} from 'widgets/hive/ui/styles.ts';
import {useApiariesStore} from 'entities/apiaries';
import {HiveTitle} from 'features/hive/ui/HiveTitle/HiveTitle.tsx';
import {ArrowToLeftIcon} from 'shared/ui/icons/arrowToLeftIcon.tsx';
import {toast} from 'react-toastify';
import {useHivesStore} from 'entities/hives';
import {SharedHive} from 'entities/hives/model/types';

const CHART_TITLES: Record<string, string> = {
  happiness: 'Индекс счастья',
  temperature: 'Температура',
  humidity: 'Влажность',
  co2: 'CO₂',
  weight: 'Масса улья',
  state: 'Состояние семьи',
};

export const Hive = () => {
  const {id, chartId} = useParams<{id: string; chartId?: string}>();
  const location = useLocation();
  const navigate = useNavigate();

  const setCurrentHiveId = useCurrentHiveStore((s) => s.setCurrentHiveId);
  const fetchById = useCurrentHiveStore((s) => s.fetchById);
  const isLoading = useCurrentHiveStore((s) => s.isLoading);
  const error = useCurrentHiveStore((s) => s.error);
  const currentHive = useCurrentHiveStore((s) => s.currentHive);

  const sharedHives = useHivesStore((s) => s.sharedHives);

  const setTitle = usePageTitleStore((s) => s.setRouteTitle);
  const setBreadcrumbs = useBreadcrumbsStore((s) => s.setCrumbs);

  useEffect(() => {
    if (!id) return;
    setCurrentHiveId(id);
    fetchById(id);

    return () => {
      setTitle('');
    };
  }, [id, setCurrentHiveId, fetchById]);

  useEffect(() => {
    toast.error(error);
  }, [error]);

  const hiveNumber = useMemo(() => currentHive?.number ?? null, [currentHive]);

  const findedShared: SharedHive | null = useMemo(() => {
    if (!Array.isArray(sharedHives)) return null;

    const finded = sharedHives.find((el) => el.ecppHiveId === currentHive?.id);
    if (finded) return finded;

    return null;
  }, [sharedHives, currentHive]);

  useEffect(() => {
    if (!id) return;

    const isAnalyticsRoot = !chartId && location.pathname.includes('/analytics');
    const isChart = Boolean(chartId);

    if (isChart) {
      const chartName = CHART_TITLES[chartId!] ?? 'График';

      setTitle(
        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
          <div
            style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}
            onClick={() => navigate(`/hive/${id}/analytics`)}
          >
            <ArrowToLeftIcon />
          </div>
          <h1 style={{fontSize: 20, fontWeight: 500}}>{chartName}</h1>
        </div>
      );

      setBreadcrumbs([
        {title: 'Моя пасека', url: '/', route: {path: '/'}},
        {title: 'Ульи', url: '/hives', route: {path: '/hives'}},
        {title: `Улей №${hiveNumber}`, url: `/hive/${id}`, route: {path: '/hive/:id'}},
        {title: 'Аналитика', url: `/hive/${id}/analytics`, route: {path: '/hive/:id/analytics'}},
        {
          title: chartName,
          url: `/hive/${id}/analytics/${chartId}`,
          route: {path: '/hive/:id/analytics/:chartId'},
        },
      ]);
      return;
    }

    setTitle(
      <HiveTitle
        number={hiveNumber}
        nextHiveId={currentHive?.id}
        prevHiveId={currentHive?.id}
        findedShared={findedShared}
      />
    );
    setBreadcrumbs([
      {title: 'Моя пасека', url: '/', route: {path: '/'}},
      {title: 'Ульи', url: '/hives', route: {path: '/hives'}},
      {title: `Улей №${hiveNumber}`, url: `/hive/${id}`, route: {path: '/hive/:id'}},
      ...(isAnalyticsRoot
        ? [{title: 'Аналитика', url: `/hive/${id}/analytics`, route: {path: '/hive/:id/analytics'}}]
        : []),
    ]);
  }, [
    id,
    hiveNumber,
    currentHive?.id,
    setTitle,
    setBreadcrumbs,
    location.pathname,
    chartId,
    navigate,
    findedShared,
  ]);

  const currentApiaryId = useApiariesStore((s) => s.currentApiaryId);
  const prevApiaryIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    prevApiaryIdRef.current = currentApiaryId ?? null;
  }, []);

  useEffect(() => {
    const prev = prevApiaryIdRef.current;
    const curr = currentApiaryId ?? null;
    if (prev !== null && prev !== curr) {
      if (location.pathname.startsWith('/hive/')) {
        prevApiaryIdRef.current = curr;
        return;
      }
      if (location.pathname !== '/hives') navigate('/hives', {replace: true});
    }
    prevApiaryIdRef.current = curr;
  }, [currentApiaryId, navigate, location.pathname]);

  const isAnalyticsRoot = !chartId && location.pathname.includes('/analytics');

  if (chartId) return <HiveChartSection />;
  if (isLoading) return <div>Загрузка…</div>;
  // if (error) return <div>Ошибка</div>;
  if (!currentHive) return null;

  return (
    <>
      <HiveHeader />
      <HiveBodyWrapper>
        {isAnalyticsRoot ? <HiveBodyAnalytics /> : <HiveBodySummary findedShared={findedShared} />}
      </HiveBodyWrapper>
    </>
  );
};
